import { TestSuites } from '@/app/features/reports/types/test-specification';
import { getFieldSetting } from '@/utils/getDefaultFieldSettings';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Resolver } from 'react-hook-form';
import {
  CustomField,
  TestSuite,
  ValidationTypes,
} from 'store/src/lib/tests/entity';
import { z } from 'zod';
import { ImageSchema, type TestFormValues } from '@app.raytd.com/common/types/test-form';

const createBasicFieldSchema = (
  field: CustomField,
  type: ValidationTypes,
  status?: string
): z.ZodTypeAny => {
  const isRequired = field.required && status !== 'draft';

  // Handle date type explicitly first
  if (field.type === 'date') {
    if (isRequired) {
      // Preprocess to handle null/undefined/empty string *before* coercion for required fields
      return z.preprocess(
        (arg) => {
          // If input is null, undefined, or empty string, return undefined to trigger required error
          if (arg === null || arg === undefined || arg === '') {
            return undefined;
          }
          // Otherwise, pass the original argument through for coercion
          return arg;
        },
        z.coerce.date({
          required_error: `${field.label} is required`,
          invalid_type_error: `${field.label} must be a valid date`,
        })
      );
    } else {
      // For optional dates, allow null/undefined and coerce valid inputs
      return z.preprocess(
        (arg) => {
          if (arg === null || arg === undefined || arg === '') {
            return null;
          }
          return arg;
        },
        z.coerce.date({
          invalid_type_error: `${field.label} must be a valid date`,
        }).nullable().optional()
      );
    }
  }

  // Handle radio type
  if (field.type === 'radio') {
    const options = field.data?.map((option: any) => option.id);

    const baseSchema = z.coerce.string().transform((val) => {
      // Handle empty/null/undefined explicitly
      if (val === 'undefined' || val === null || val === '') {
          return null;
      }
      // Check if it looks like a UUID before returning
      const uuidRegex = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
      const cleaned = uuidRegex.test(String(val)) ? val : ''; // Ensure val is string for test
      return cleaned === '' ? null : String(cleaned); // Return string or null
    });

    if (isRequired) {
      return baseSchema.pipe(
        z
          .string()
          .nullable()
          .refine((val): val is string => val !== null, { // Type guard for refinement
            message: `${field.label} is required`,
          })
          .refine((val) => options?.includes(val), { // val is guaranteed string here
            message: `Invalid option selected for ${field.label}`,
          })
      );
    }

    return baseSchema.pipe(
      z
        .string()
        .nullable()
        .optional()
        .refine((val) => val === null || options?.includes(val), { // val is string | null here
          message: `Invalid option selected for ${field.label}`,
        })
    );
  }

  // Handle select type
  if (field.type === 'select') {
    const options = field.data?.map((option: any) => option.id);

    const baseSchema = z.preprocess((val) => {
      // Handle empty/null/undefined explicitly
      if (val === 'undefined' || val === null || val === '') {
        return null;
      }
      return String(val);
    }, z.string().nullable());

    if (isRequired) {
      const refinedSchema = baseSchema.refine((val): val is string => val !== null, {
        message: `${field.label} is required`,
      });

      if (field.allow_add) {
        return refinedSchema;
      }

      return refinedSchema.refine((val) => !options || options.includes(val), {
        message: `Invalid option selected for ${field.label}`,
      });
    }

    const optionalSchema = baseSchema;
    if (field.allow_add) {
      return optionalSchema.optional();
    }

    return optionalSchema.refine((val) => val === null || !options || options.includes(val), {
      message: `Invalid option selected for ${field.label}`,
    }).optional();
  }

  // Handle validation types (from field.validations)
  switch (type) {
    case 'boolean':
      // Use z.preprocess for required booleans to handle non-boolean inputs gracefully
      return isRequired
       ? z.preprocess(
           (val) => {
             if (val === undefined || val === null || val === '') return undefined;
             return val === 'true' || val === true;
           }, 
           z.boolean({ required_error: `${field.label} is required`})
         )
       : z.boolean().nullable().optional();
    case 'numeric':
      return isRequired
        ? z.preprocess(
            (val) => {              
              // Explicitly map empty strings and null/undefined to undefined for required check
              if (val === null || val === undefined || val === '') {
                return undefined;
              }
              
              // Attempt to remove non-numeric characters *before* coercion
              const cleanedVal = typeof val === 'string' ? val.replace(/[^0-9.-]/g, '') : val;
              
              // If cleaning results in empty or invalid number (e.g. just "-" or "."), treat as undefined for required check
              if (cleanedVal === '' || (typeof cleanedVal === 'string' && isNaN(Number(cleanedVal)))) {
                return undefined;
              }
              
              return cleanedVal;
            },
            z.coerce.number({
              required_error: `${field.label} is required`,
              invalid_type_error: `${field.label} must be a valid number`
            })
          )
        : z.preprocess( // Also preprocess optional numerics for cleaner coercion
            (val) => {              
              // Map empty string or null/undefined to null for optional field
              if (val === null || val === undefined || val === '') {
                return null;
              }
              
              const cleanedVal = typeof val === 'string' ? val.replace(/[^0-9.-]/g, '') : val;

              if (cleanedVal === '' || isNaN(Number(cleanedVal))) {
                return null;
              }
              
              return cleanedVal;
            },
            z.coerce.number({
                invalid_type_error: `${field.label} must be a valid number`
            }).nullable().optional()
          );
    default: { // String type or others
      let schema = z.coerce.string();

      if (field.type === 'text') {
        schema = schema.max(255, `${field.label} cannot exceed 255 characters`);
      } else if (field.type === 'textarea') {
        schema = schema.max(5000, `${field.label} cannot exceed 5000 characters`);
      }

      return isRequired
        ? schema.min(1, `${field.label} is required`)
        : schema.nullable().optional();
    }
  }
};

const createArrayFieldSchema = (field: CustomField, status?: string) => {
  const isRequired = field.required && status !== 'draft';
  const options = field.data?.map((option: any) => option.id);

  let arraySchema = z.array(z.string().min(1, 'Selection cannot be empty'));

  if (isRequired) {
    arraySchema = arraySchema.min(1, `${field.label} must have at least one item`);
  }

  // Enforce options check if allow_add is false
  if (!field.allow_add && options?.length > 0) {
    return arraySchema.refine(
      (items) => items.every((item) => options.includes(item)),
      { message: `Invalid option selected for ${field.label}` }
    ).optional().transform((val) => val || []);
  }

  return isRequired
    ? arraySchema.transform((val) => val || []) // Handle null/undefined
    : arraySchema
        .nullable()
        .optional()
        .transform((val) => val || []); // Handle null/undefined
};

const createCustomFieldSchema = (field: CustomField, status?: string): z.ZodTypeAny => {
  
  if (!field.active) {
    return z.never();
  }

  const isMultiple = field.selection_type === 'multiple';
  
  const schema = isMultiple
    ? createArrayFieldSchema(field, status)
    : createBasicFieldSchema(
        field,
        getValidationTypeFromFieldType(field),
        status
      );
      
  return schema;
};

// Map custom field types to validation types
const getValidationTypeFromFieldType = (field: CustomField): ValidationTypes => {
  
  const validationType = (() => {
    switch (field.type) {
      case 'number':
      case 'scrollable_number':
        return 'numeric';
      case 'checkbox':
      case 'switch':
        return 'boolean';
      default:
        // For text, textarea, raytd, select, radio, date and others, use the validations field or default to string
        const fallbackType = (field.validations as ValidationTypes) || 'string';
        return fallbackType;
    }
  })();
  
  return validationType;
};

const buildCustomFieldValidation = (customFields: CustomField[] = [], status?: string) => {
  const schemas = (customFields || []).reduce<Record<string, z.ZodTypeAny>>(
    (acc, field) => {
      if (field.active !== false) {
        // Remove the superRefine wrapper and use the schema directly.
        // Zod's base schema validation is sufficient.
        acc[field.field_id] = createCustomFieldSchema(field, status);
      } else {
        // For inactive fields, allow them to be present but ignore them
        acc[field.field_id] = z.any().optional();
      }

      return acc;
    },
    {}
  );

  return z.object(schemas);
};

// Base validation schema (always required fields)
const baseSchema = z.object({
  assessmentId: z.coerce.number().min(1, 'Inspection is required'),
  assetId: z.coerce.number().min(1, 'Asset is required'),
  test_specification_id: z.coerce.number().min(1, 'Test is required'),
  status: z.enum(['archived', 'draft', 'qa', 'published']),
  images: z.array(ImageSchema).optional(),
});


const fields = [
  'element',
  'element_name',
  'element_id',
  'observations',
  'recommendations',
  'images',
  'result',
  'building_id',
  'level_id',
  'area_id',
] as const;

const buildFieldSchema = (currentTestSuite: TestSuite, status?: string) => {
  const dynamicValidation: Record<string, any> = {};
  fields?.forEach((fieldName) => {
    const fieldSettings = getFieldSetting(
      fieldName,
      currentTestSuite?.type,
      currentTestSuite?.settings || []
    );

    const isActive = fieldSettings.active;
    // Relax requirements for draft status
    const isRequired = fieldSettings.required && status !== 'draft';

    if (isActive) {
      // Field is active
      let field = fieldName;
      const fieldSchema = (() => {
        switch (fieldName) {
          case 'element': {
            const idSchema = z.union([
              z.number().int().positive(),
              z.string().startsWith('temp_'),
            ]);
            return isRequired
              ? idSchema.refine(val => val !== null && val !== undefined, {
                message: 'An element must be selected',
              })
              : idSchema.nullable().optional();
          }
          case 'element_name':
          case 'element_id':
          case 'observations':
          case 'recommendations':
            return isRequired
              ? z.string().min(1, `${fieldName} is required`)
              : z.string().nullable().optional();
          case 'images':
            // fieldName = 'images';
            // console.debug('images', { fieldName, isActive, isRequired });
            return isRequired
            ? z.array(ImageSchema)
                .refine(
                  (images) => (images?.filter(img => !img.deleted) || []).length >= 1,
                  'At least one non-deleted image is required'
                )
            : z.array(ImageSchema)
                .nullable()
                .optional();

              /**
               * @TODO : Don't allow deleted images
               */
          case 'result':
            return isRequired
              ? z.string().min(1, `A result is required`)
              : z.string().nullable().optional();

          case 'building_id':
          case 'level_id':
          case 'area_id': {
            // Define a reusable schema that accepts a positive integer OR a temporary string ID
            const idSchema = z.union([
              z.number().int().positive(),
              z.string().startsWith('temp_'),
            ]);

            // Apply required rule only to building_id if needed
            if (fieldName === 'building_id' && isRequired) {
              return idSchema.refine(val => val !== null && val !== undefined, {
                message: 'A building must be selected',
              });
            }
            
            // For other IDs (or optional building_id), allow null/optional
            return idSchema.nullable().optional();
          }

          default:
            return z.any().optional();
        }
      })();

      dynamicValidation[field] = fieldSchema;
    }
  });

  return z.object(dynamicValidation);
};

const buildValidationSchema = (
  testSuite: TestSuite | undefined,
  status?: string
): z.ZodTypeAny => {
  if (!testSuite) {
    return baseSchema;
  }

  const fieldValidationSchema = buildFieldSchema(testSuite, status);
  const customValidationSchema = buildCustomFieldValidation(
    testSuite.custom_fields,
    status
  );

  const schema = baseSchema.extend({
    ...fieldValidationSchema.shape,
    custom: customValidationSchema,
  });

  return schema;
};

// Pure, hook-free readiness check: validates the given form values as if the
// record were transitioning to `targetStatus`, regardless of the record's
// actual/current status. The RHF resolver (below) validates against the
// record's *live* status and therefore relaxes suite-required fields while
// status is still 'draft' — which is correct for autosaving an in-progress
// draft, but wrong for deciding whether a QA/Final transition should be
// allowed. Callers gating a status-transition control should use this
// instead of resolver-derived validity.
export const isSubmissionReadyForStatus = async (
  data: TestFormValues,
  testSuites: TestSuites[],
  targetStatus: string
): Promise<boolean> => {
  const specId = data.test_specification_id;
  if (!specId) return false;

  const testSuite = testSuites.find((suite) =>
    suite.specifications.some((spec) => spec.id === specId)
  ) as unknown as TestSuite;

  if (!testSuite) return false;

  const schema = buildValidationSchema(testSuite, targetStatus);
  const result = await schema.safeParseAsync({ ...data, status: targetStatus });
  return result.success;
};

const schemaCache = new Map<string, z.ZodTypeAny>();

export const useDynamicResolver = (testSuites: TestSuites[]): Resolver<TestFormValues> => {
  const prevTestSuitesRef = useRef(testSuites);

  useEffect(() => {
    if (prevTestSuitesRef.current !== testSuites) {
      console.log('[Validator] Clearing schema cache because testSuites changed');
      schemaCache.clear();
      prevTestSuitesRef.current = testSuites;
    }
  }, [testSuites]);

  const validateData = useCallback(
    async (data: TestFormValues) => {
      const specId = data.test_specification_id;
      const status = data.status;
      const cacheKey = `${specId}-${status}`;
      
      // CRITICAL FIX: Handle null/undefined/0 test_specification_id gracefully
      // Instead of clearing all form values, preserve the data and show appropriate validation errors
      if (!specId || specId === null || specId === undefined || specId === 0) {
        // console.log(`[Validator] No test specification selected (specId: ${specId}). Preserving form data.`);
        return {
          values: {},
          errors: {
            test_specification_id: {
              message: 'Please select a test specification.',
              type: 'required'
            }
          },
        };
      }
      
      // Clear any potential stale cache entries periodically to prevent sync issues
      if (schemaCache.size > 20) {
        // console.log('[Validator] Clearing schema cache to prevent stale entries');
        schemaCache.clear();
      }
      
      let schema = schemaCache.get(cacheKey);
      //let schema; // Force schema rebuild by not checking cache

      if (!schema) {
        const foundSuite = testSuites.find((suite) =>
          suite.specifications.some((spec) => spec.id === specId)
        );

        const testSuite = foundSuite as unknown as TestSuite;

        if (!testSuite) {
          //console.log(`[Validator] No testSuite found for specId: ${specId}`);
          // 🔧 PRESERVE form data even when test suite is not found
          return {
            values: {},
            errors: {
              _form: [
                {
                  message: `Configuration error: No test suite found for specification ID ${specId}.`,
                  code: 'custom',
                  path: [],
                },
              ],
            },
          };
        }
        schema = buildValidationSchema(testSuite, status);
        schemaCache.set(cacheKey, schema); // Re-enable caching but with proper clearing
        // console.log(`[Validator] Schema built and cached for specId: ${specId}`, schema);
      } else {
        // console.log(`[Validator] Cache hit for specId: ${specId}`);
      }

      try {
        // console.log(`[Validator] Attempting to parse data with schema for specId: ${specId}`);
        
        // ENHANCED LOGGING: Track location values before and after validation
        const inputLocationValues = {
          building_id: data.building_id,
          level_id: data.level_id,
          area_id: data.area_id
        };
        // console.log('[Validator] 📍 Location values BEFORE parseAsync:', inputLocationValues);
        
        const validatedData = await schema.parseAsync(data);
        
        // ENHANCED LOGGING: Track location values after validation
        const outputLocationValues = {
          building_id: validatedData.building_id,
          level_id: validatedData.level_id,
          area_id: validatedData.area_id
        };
        // console.log('[Validator] 📍 Location values AFTER parseAsync:', outputLocationValues);
        
        // Check for corruption
        const locationCorrupted = 
          inputLocationValues.building_id !== outputLocationValues.building_id ||
          inputLocationValues.level_id !== outputLocationValues.level_id ||
          inputLocationValues.area_id !== outputLocationValues.area_id;
        
        if (locationCorrupted) {
          // // console.error('[Validator] 🚨 SCHEMA VALIDATION CORRUPTED LOCATION VALUES!', {
          //   input: inputLocationValues,
          //   output: outputLocationValues,
          //   changes: {
          //     building_id: `${inputLocationValues.building_id} → ${outputLocationValues.building_id}`,
          //     level_id: `${inputLocationValues.level_id} → ${outputLocationValues.level_id}`,
          //     area_id: `${inputLocationValues.area_id} → ${outputLocationValues.area_id}`
          //   }
          // });
        } else {
          // console.log('[Validator] ✅ Schema validation preserved location values correctly');
        }
        
        // console.log('[Validator] Validation successful for specId:', specId, 'Validated Data:', validatedData);
        
        
        return {
          values: validatedData, // Return validatedData, NOT original data
          errors: {},   // Explicitly clear all errors with fresh object
        };
      } catch (error) {
        // console.debug('[Validator] Validation Error caught for specId:', specId, 'Raw Error:', error);
        if (error instanceof z.ZodError) {
          // Manually format errors to preserve nested structure for react-hook-form.
          // error.formErrors.fieldErrors flattens them, which loses context for custom fields.
          const formattedErrors: Record<string, any> = {};
          for (const issue of error.issues) {
            const path = issue.path;
            let current: Record<string, any> = formattedErrors;
            for (let i = 0; i < path.length - 1; i++) {
              const segment = path[i];
              if (!current[segment]) {
                current[segment] = {};
              }
              current = current[segment];
            }
            const lastSegment = path[path.length - 1];
            // Only set the first error for a given path to avoid complex error objects
            if (!current[lastSegment]) {
              current[lastSegment] = { message: issue.message, type: issue.code };
            }
          }

          return {
            values: {} as any,
            errors: formattedErrors,
          };
        }

        // Handle non-Zod errors with more context
        // console.error('[Validator] Non-Zod error during validation:', error);
        return {
          values: {} as any,
          errors: {
            _form: [
              {
                message: 'Validation failed due to an unexpected error.',
                code: 'custom',
                path: [],
                details:
                  error instanceof Error ? error.message : 'Unknown error',
              },
            ],
          },
        };
      }
    },
    [testSuites]
  );

  return useMemo(() => validateData, [validateData]);
};
